import { RenderBuilderWithSSR } from "@faire/builder-core/BuilderComponents/RenderBuilderWithSSR";
import { RenderWithBuilder } from "@faire/builder-core/BuilderComponents/RenderWithBuilder";
import { RenderBuilderWithSSRProps } from "@faire/builder-core/BuilderComponents/types";
import { FE_UPLT_HISTORY_TIMING_V2_250401 } from "@faire/retailer-settings/FE_UPLT_HISTORY_TIMING_V2_250401";
import { Footer } from "@faire/retailer-visitor-shared/components/Footer/index";
import { SlideIn } from "@faire/retailer-visitor-shared/components/SlideIn";
import { PageInitialized } from "@faire/retailer-visitor-shared/events";
import { useAssignEffect } from "@faire/retailer-visitor-shared/settings/useEffectAssignWhen";
import { useFrontendSetting } from "@faire/retailer-visitor-shared/settings/useFrontendSetting";
import {
  useAssignSettingLoggedOutHomepageCampaign,
  useSettingLoggedOutHomepageCampaign,
} from "@faire/retailer-visitor-shared/settings/useSettingLoggedOutHomepageCampaign";
import { BestsellersBanner } from "@faire/retailer-visitor-shared/views/home/logged-out-homepage/__internal__/components/BestsellersBanner/BestsellersBanner";
import { BlogCarousel } from "@faire/retailer-visitor-shared/views/home/logged-out-homepage/__internal__/components/BlogCarousel/BlogCarousel";
import { BottomSignUpBanner } from "@faire/retailer-visitor-shared/views/home/logged-out-homepage/__internal__/components/BottomSignUpBanner/BottomSignUpBanner";
import { BestsellerProductsCarousel } from "@faire/retailer-visitor-shared/views/home/logged-out-homepage/__internal__/components/homepage-v2-only/BestsellerProductsCarousel/BestsellerProductsCarousel";
import { BrandValueBannerWithV2Design } from "@faire/retailer-visitor-shared/views/home/logged-out-homepage/__internal__/components/homepage-v2-only/BrandValueBannerV2/BrandValueBannerWithV2Design";
import { CategoryBestBrandsWithV2Design } from "@faire/retailer-visitor-shared/views/home/logged-out-homepage/__internal__/components/homepage-v2-only/CategoryBestBrandsBannerV2/CategoryBestBrandsWithV2Design";
import { ForAnyRetailerBanner } from "@faire/retailer-visitor-shared/views/home/logged-out-homepage/__internal__/components/homepage-v2-only/ForAnyRetailerBanner/ForAnyRetailerBanner";
import { TopHeaderBanner } from "@faire/retailer-visitor-shared/views/home/logged-out-homepage/__internal__/components/TopHeaderBanner/TopHeaderBanner";
import { WeMakeItSimpleBanner } from "@faire/retailer-visitor-shared/views/home/logged-out-homepage/__internal__/components/WeMakeItSimpleBanner/WeMakeItSimpleBanner";
import { useCategoryBrandsBannerQuery } from "@faire/retailer-visitor-shared/views/home/logged-out-homepage/__internal__/DataFetching/useCategoryBrandsBannerQuery";
import { trackLoggedOutRetailersLoggedOutHomeRedesignView } from "@faire/web-api--source/events/loggedOutRetailers/view/loggedOutHomeRedesign";
import { trackRetailersPageViewView } from "@faire/web-api--source/events/retailers/view/pageView";
import { markPageJsExecuted } from "@faire/web/common/performance/TrackPerformanceBreakdown/markPageJsExecuted";
import { useUserPerceivedLoadTimingWithKey } from "@faire/web/common/performance/userPerceivedLoadTiming/useUserPerceivedLoadTiming";
import { useAssignSettingBuilderXpWithThreeVariants } from "@faire/web/common/settings/useSettingBuilderExperiments";
import { LoadingSkeleton } from "@faire/web/slate/LoadingSkeleton";
import { getSpacing } from "@faire/web/slate/spacing";
import { useDoActionWhenVisible } from "@faire/web/ui/hooks/useDoActionWhenVisible";
import { WhenVisibilityIsImminent } from "@faire/web/ui/VisibilitySensor/WhenVisibilityIsImminent";
import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";

import { useIsLoggedOutHomepageV2 } from "./__internal__/useIsLoggedOutHomepageV2";

const PAGE_JS_EXECUTED_MARK = markPageJsExecuted();
export const LoggedOutHomePageBase: React.FC<{
  builderVideoHeaderProps: RenderBuilderWithSSRProps | null;
}> = ({ builderVideoHeaderProps }) => {
  const { isFetching, dataUpdatedAt } = useCategoryBrandsBannerQuery();

  useAssignEffect(FE_UPLT_HISTORY_TIMING_V2_250401, true);
  const isSettingHistoryTimingV2 = useFrontendSetting(
    FE_UPLT_HISTORY_TIMING_V2_250401
  );

  const loadTimingRef = useUserPerceivedLoadTimingWithKey({
    name: "page-home-logged-out",
    pageLoadKey: "page-home-logged-out-redesign",
    numComponents: 2,
    isPageLoading: isFetching,
    pageJsExecutedMark: PAGE_JS_EXECUTED_MARK,
    enableTrackPerformanceBreakdown: "internal-tracking",
    dataUpdatedAt,
    enablePageUpdateFix: isSettingHistoryTimingV2 ? "v2" : undefined,
  });
  const { pathname } = useLocation();
  const ref = useDoActionWhenVisible(() =>
    trackRetailersPageViewView(
      "",
      "",
      "",
      "home",
      pathname,
      "page-home-logged-out-redesign"
    )
  );

  const isHomepageRedesignV2 = useIsLoggedOutHomepageV2();
  const assignSettingBuilderXpWithThreeVariants =
    useAssignSettingBuilderXpWithThreeVariants();

  const assignSettingLoggedOutHomepageCampaign =
    useAssignSettingLoggedOutHomepageCampaign();

  const loggedOutHomepageCampaignSetting =
    useSettingLoggedOutHomepageCampaign();
  const showLoggedOutHomepageCampaign =
    isHomepageRedesignV2 && loggedOutHomepageCampaignSetting;

  useEffect(() => {
    if (isHomepageRedesignV2) {
      assignSettingLoggedOutHomepageCampaign();
    }
  }, [assignSettingLoggedOutHomepageCampaign, isHomepageRedesignV2]);

  useEffect(() => {
    PageInitialized.publish();
  }, []);

  return (
    <div
      style={{ background: "#FBF8F6", width: "100%" }}
      ref={ref}
      data-test-id="logged-out-homepage"
    >
      {builderVideoHeaderProps != null ? (
        <RenderBuilderWithSSR
          {...builderVideoHeaderProps}
          loadTimingRef={loadTimingRef}
          suspenseFallback={
            <LoadingSkeleton
              aspectRatio={{ mobileAndAbove: "9/10", tabletAndAbove: "16/9" }}
              width="100%"
            />
          }
          onViewEvent={() => {
            assignSettingBuilderXpWithThreeVariants();
            trackLoggedOutRetailersLoggedOutHomeRedesignView(
              "big_video_header_banner",
              "v2"
            );
          }}
        />
      ) : (
        <TopHeaderBanner loadTimingRef={loadTimingRef} />
      )}

      <CategoryBestBrandsWithV2Design loadTimingRef={loadTimingRef} />
      {isHomepageRedesignV2 ? (
        <>
          <SlideIn offset={getSpacing("3x")}>
            <RenderWithBuilder
              modelName="logged-out-homepage-were-faire-banner"
              targets={{
                loggedOutHomepageCampaign: showLoggedOutHomepageCampaign,
              }}
              onViewEvent={() => {
                trackLoggedOutRetailersLoggedOutHomeRedesignView(
                  "were_faire_banner",
                  "v2"
                );
              }}
            />
          </SlideIn>
          <WhenVisibilityIsImminent>
            <BestsellerProductsCarousel />
          </WhenVisibilityIsImminent>
          {/* for loggedout homepage campaign, for any retailer banner is migrated to Builder*/}
          {!showLoggedOutHomepageCampaign ? <ForAnyRetailerBanner /> : null}
          <SlideIn offset={getSpacing("3x")}>
            <RenderWithBuilder
              modelName="logged-out-homepage-explore-categories-carousel"
              onViewEvent={() => {
                trackLoggedOutRetailersLoggedOutHomeRedesignView(
                  "explore_categories_carousel",
                  "v2"
                );
              }}
              targets={{
                loggedOutHomepageCampaign: showLoggedOutHomepageCampaign,
              }}
            />
          </SlideIn>
        </>
      ) : null}
      <SlideIn offset={getSpacing("3x")}>
        {isHomepageRedesignV2 ? null : <BestsellersBanner />}
      </SlideIn>
      <BrandValueBannerWithV2Design />
      <SlideIn offset={getSpacing("3x")}>
        <WeMakeItSimpleBanner />
      </SlideIn>
      <WhenVisibilityIsImminent>
        <BlogCarousel />
      </WhenVisibilityIsImminent>
      <SlideIn offset={getSpacing("3x")}>
        <BottomSignUpBanner />
      </SlideIn>
      <Footer />
    </div>
  );
};
